/**
 * @file /src/lib/utils/fit/profileMatch.ts
 *
 * @description
 * Pure helpers that score how well a candidate's PROFILE matches a job, with the same two metrics the resume
 * match uses: keywords (atomic job terms) and semantic (the job's requirements). Both lists come from one job
 * description analysis (see jdAnalysis.ts). The decision model (Jev) answers one question per term and per
 * requirement; this module builds those questions, splits them into requests the model can take, and turns the
 * answers into scores. No AI or network imports.
 *
 * Scoring: each half is a weighted average (core 3, supporting 2, bonus 1, the same ratios as the old fit score).
 * Keyword credit by support level: stated 1, implied 0.75, bridge 0.5, none 0. Semantic credit is the model's
 * 0 to 2 score divided by 2. Knockout requirements (location, on-site days, work authorization, salary) are not part
 * of either half; see knockouts.ts.
 * Overall is the 50/50 average of the halves, and the status thresholds are the old fit ones (red up to 33,
 * yellow up to 66, green above).
 */

import {
  buildJevRequest,
  hashResumeText,
  parseJevResponse,
  type MatchDocument,
  type SupportLevel,
} from "@/lib/utils/document/keywordMatch";
import type { DerivedTerm } from "@/lib/utils/fit/jdAnalysis";
import { isKnockoutRequirement, MAX_QUESTIONS_PER_REQUEST } from "@/lib/utils/fit/knockouts";
import type { JdImportance, JdRequirement, ProfileMatchReport, ProfileRequirementMatch } from "@/types";

export { MAX_QUESTIONS_PER_REQUEST };

const WEIGHT: Record<JdImportance, number> = { core: 3, supporting: 2, bonus: 1 };
const SUPPORT_CREDIT: Record<SupportLevel, number> = { stated: 1, implied: 0.75, bridge: 0.5, none: 0 };

const REQUIREMENT_SCALE = [
  "Not shown",
  "Touched on without a specific example",
  "Shown with a specific example",
];
const SHOWN_AT = 1.5;
const PARTIAL_AT = 0.75;

export function statusFromScore(score: number): "red" | "yellow" | "green" {
  if (score <= 33) return "red";
  if (score <= 66) return "yellow";
  return "green";
}

/** Hash of the job description inputs; a change means stored analysis and scores are stale. */
export function hashJobDescription(jd: { title?: string; company?: string; description?: string }): string {
  return hashResumeText([jd.title ?? "", jd.company ?? "", jd.description ?? ""].join("\n"));
}

type JevRequest = { model: string; state: Record<string, string>; questions: Record<string, unknown> };

/**
 * Requests for the decision model: one Choice question per term (keys q0, q1, ...) and one Score question per
 * scoreable requirement (keys r0, r1, ... in the order of the requirements that are not knockouts), split into chunks
 * of MAX_QUESTIONS_PER_REQUEST. Every chunk repeats the same state.
 */
export function buildProfileMatchRequests(args: {
  /** Text of the document being checked: the profile (default) or the generated resume. */
  profileText: string;
  jobDescriptionText: string;
  terms: DerivedTerm[];
  requirements: (Pick<JdRequirement, "requirement" | "category"> & { terms?: string[] })[];
  document?: MatchDocument;
}): JevRequest[] {
  const doc = args.document ?? "profile";
  const base = buildJevRequest({
    profileText: args.profileText,
    jobDescriptionText: args.jobDescriptionText,
    terms: args.terms.map((t) => t.term),
    document: doc,
  }) as JevRequest;

  const all: Record<string, unknown> = { ...base.questions };
  args.requirements
    .filter((r) => !isKnockoutRequirement(r))
    .forEach((r, i) => {
      all[`r${i}`] = {
        type: "score",
        instructions:
          doc === "resume"
            ? `How well does the resume show the candidate has done this: "${r.requirement}"?`
            : `How well does the candidate profile show they have done this: "${r.requirement}"?`,
        criteria: REQUIREMENT_SCALE,
      };
    });

  const keys = Object.keys(all);
  const requests: JevRequest[] = [];
  for (let i = 0; i < keys.length; i += MAX_QUESTIONS_PER_REQUEST) {
    const questions: Record<string, unknown> = {};
    for (const k of keys.slice(i, i + MAX_QUESTIONS_PER_REQUEST)) questions[k] = all[k];
    requests.push({ model: base.model, state: base.state, questions });
  }
  return requests;
}

/** Merges the answers of several responses into one body. */
export function mergeJevAnswers(bodies: unknown[]): { answers: Record<string, unknown> } {
  const answers: Record<string, unknown> = {};
  for (const b of bodies) Object.assign(answers, (b as { answers?: Record<string, unknown> })?.answers ?? {});
  return { answers };
}

/** Question keys that got no answer. The service treats any as a failure rather than scoring a gap as "none". */
export function missingAnswerKeys(merged: { answers: Record<string, unknown> }, requests: JevRequest[]): string[] {
  return requests.flatMap((r) => Object.keys(r.questions)).filter((k) => merged.answers[k] === undefined);
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const pct = (credit: number, weight: number) => (weight > 0 ? Math.round((credit / weight) * 100) : null);

/** Turns the merged answers into the report. A missing or unrecognized answer counts as the lowest level. */
export function scoreProfileMatch(args: {
  body: { answers?: Record<string, unknown> };
  terms: DerivedTerm[];
  requirements: JdRequirement[];
  profileText: string;
  jdHash: string;
  now?: Date;
}): ProfileMatchReport {
  const matches = parseJevResponse(args.body, args.terms);
  const answers = (args.body.answers ?? {}) as Record<string, { score?: number }>;

  let termCredit = 0;
  let termWeight = 0;
  for (const m of matches) {
    const w = WEIGHT[m.importance] ?? 1;
    termWeight += w;
    termCredit += w * SUPPORT_CREDIT[m.support];
  }

  let reqCredit = 0;
  let reqWeight = 0;
  let asked = 0; // index among the scoreable requirements, matching the question keys
  // Knockouts are not competencies: they are answered separately (see knockouts.ts) and never scored here.
  const requirements: ProfileRequirementMatch[] = args.requirements
    .filter((r) => !isKnockoutRequirement(r))
    .map((r) => {
      const raw = answers[`r${asked++}`]?.score;
      const score = typeof raw === "number" ? clamp(raw, 0, 2) : 0;
      const w = WEIGHT[r.importance] ?? 1;
      reqWeight += w;
      reqCredit += w * (score / 2);
      return {
        requirementId: r.id,
        requirement: r.requirement,
        importance: r.importance,
        score: Math.round(score * 100) / 100,
        level: score >= SHOWN_AT ? "shown" : score >= PARTIAL_AT ? "partial" : "none",
        terms: r.terms,
      };
    });

  const keywordScore = pct(termCredit, termWeight);
  const semanticScore = pct(reqCredit, reqWeight);
  const halves = [keywordScore, semanticScore].filter((x): x is number => x !== null);
  const overallScore = halves.length ? Math.round(halves.reduce((a, b) => a + b, 0) / halves.length) : 0;

  return {
    terms: matches.map((m, i) => ({
      term: m.term,
      importance: m.importance,
      support: m.support,
      ...(m.pSupported !== undefined ? { pSupported: m.pSupported } : {}),
      requirementIds: args.terms[i].requirementIds,
    })),
    requirements,
    keywordScore,
    semanticScore,
    overallScore,
    overallStatus: statusFromScore(overallScore),
    matchedAt: (args.now ?? new Date()).toISOString(),
    profileHash: hashResumeText(args.profileText),
    jdHash: args.jdHash,
  };
}
